/**
 * The agent wallet's wire pieces P3 adds beside P1's module (spec 2026-10-15 §1, §2; contract C5): the purchase
 * ask's renderer (the tool names are agent-sdk's BUY_TOOL and BUY_REPORT_TOOL), the server → runner freeze frame and the server → web UPI link (zod schemas the
 * event registry validates), and the one way every surface builds or reads a purchase ask's result.
 *
 * Every ending of a purchase ask is an ALLOW whose updatedInput is a BuyToolResult. A deny would reach the model as
 * the kernel's fixed "the tool was NOT run" sentence (agent-sdk strategy.ts denyMessage) and lose the purchase id
 * buy_report needs.
 */
import { z } from 'zod';
import type { InteractionOutcome } from '../types/interaction.js';
import { AGENT_BUDGET_CURRENCIES } from './constants.js';
import type { BuyToolResult, BuyToolStatus, PurchaseFreezeFrame } from './types.js';

/** The purchase card's renderer. Minted only by the box's buy tool, never authorable by a model. */
export const PURCHASE_RENDERER = 'purchase';
/**
 * A PostgREST `or` filter that passes every interaction but a purchase card. A card that is not custom has a NULL
 * renderer, which a bare `neq` would drop. The sweeps that withdraw a task's asks on their own (a typed or spoken
 * message's supersede, the turn-end orphan sweep, a rewind, the terminal-task lane) use it, so buy's card waits for
 * the person; the purchase sweep owns its ending.
 */
export const NOT_A_PURCHASE_CARD = `renderer.is.null,renderer.neq.${PURCHASE_RENDERER}`;
/** The list as given, when it names every BuyToolStatus; never otherwise (the reverse of its satisfies). */
type EveryOf<All, List extends readonly All[]> = [All] extends [List[number]] ? List : never;
const BUY_TOOL_STATUS_LIST = ['pay_step_done', 'declined', 'expired'] as const satisfies readonly BuyToolStatus[];
/** Every ending buy reads. Both ways: a status here that BuyToolStatus lacks, or a BuyToolStatus left out, fails to compile. */
export const BUY_TOOL_STATUSES: EveryOf<BuyToolStatus, typeof BUY_TOOL_STATUS_LIST> = BUY_TOOL_STATUS_LIST;

/** The longest UPI app link carried; a real one is a few hundred characters. */
export const UPI_LINK_MAX = 2048;

/**
 * PURCHASE_FREEZE (server → runner): the box-wide payment freeze, or its lift. Tied to PurchaseFreezeFrame: a field the
 * frame carries and the schema drops (which a parse would strip without a word) fails to compile here.
 */
export const PurchaseFreezePayloadSchema = z.object({
  freeze: z
    .object({
      purchaseId: z.string().min(1),
      taskId: z.string().min(1),
      shopHost: z.string().min(1),
      approvedMinor: z.number().int().nonnegative(),
      currency: z.enum(AGENT_BUDGET_CURRENCIES),
      expiresAtMs: z.number().int().positive(),
    })
    .nullable(),
  closePaymentPage: z.boolean().optional(),
}) satisfies z.ZodType<PurchaseFreezeFrame>;

/** PURCHASE_UPI_LINK (server → web): a shop's UPI app link, to open on the person's own phone. */
export const PurchaseUpiLinkPayloadSchema = z.object({
  purchaseId: z.string().min(1),
  link: z.string().startsWith('upi://pay?').max(UPI_LINK_MAX),
});

/** The interaction outcome every purchase ask ends with: an allow carrying the result the agent reads. */
export function purchaseEndingOutcome(result: BuyToolResult): InteractionOutcome {
  return { behavior: 'allow', updatedInput: { ...result } };
}

/** A purchase ask's result, read back off an outcome's updatedInput; null for anything else, an empty purchase id or message included. */
export function buyToolResultOf(value: unknown): BuyToolResult | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.purchaseId !== 'string' || v.purchaseId.length === 0 || typeof v.message !== 'string' || v.message.length === 0) return null;
  if (!BUY_TOOL_STATUSES.includes(v.status as BuyToolStatus)) return null;
  return {
    status: v.status as BuyToolStatus,
    purchaseId: v.purchaseId,
    message: v.message,
    ...(v.timedOut === true ? { timedOut: true } : {}),
  };
}
